// @flow
import React from 'react';
import SvgIcon from 'material-ui/SvgIcon';
import FontIcon from 'material-ui/FontIcon';
import Avatar from 'material-ui/Avatar';
import Chip from 'material-ui/Chip';

import { formatAltitude } from 'utils/distance';
import { leftPad } from 'utils/datetime';
import { getFeatureColor } from 'utils/mapStyle';

import UavIcon from 'assets/icons/uav.svg';
import MilitaryIcon from 'assets/icons/military.svg';
import GlidingIcon from 'assets/icons/gliding.svg';
import ParachutingIcon from 'assets/icons/parachuting.svg';
import AirDisplayIcon from 'assets/icons/air_display.svg';
import FireworksIcon from 'assets/icons/fireworks.svg';

import GliderIcon from 'assets/icons/gliding.svg';
import AirlinerIcon from 'assets/sprites/sources/airliner.svg';
import MotorplaneIcon from 'assets/sprites/sources/motorplane.svg';
import UavPropsheetIcon from 'assets/sprites/sources/uav.svg';
import HelicopterIcon from 'assets/sprites/sources/helicopter.svg';
import ParagliderIcon from 'assets/sprites/sources/paraglider.svg';
import GroundStationIcon from 'assets/sprites/sources/ground-station.svg';
import GroundVehicleIcon from 'assets/sprites/sources/ground-vehicle.svg';

import hexRgb from 'hex-rgb';
import sortBy from 'lodash/sortBy';

const DATE_LOCALIZATION_OPTIONS = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
};

const MONTH_ORIG_RE = /(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)/g;
const MONTH_SUBST = {
  JAN: 'jan.',
  FEB: 'feb.',
  MAR: 'márc.',
  APR: 'ápr.',
  MAY: 'máj.',
  JUN: 'jún.',
  JUL: 'júl.',
  AUG: 'aug.',
  SEP: 'szept.',
  OCT: 'okt.',
  NOV: 'nov.',
  DEC: 'dec.',
};

const WEEKDAY_ORIG_RE = /(MON|TUE|WED|THU|FRI|SAT|SUN|HOLIDAY)/g;
const WEEKDAY_SUBST = {
  MON: 'H',
  TUE: 'K',
  WED: 'Sze',
  THU: 'Cs',
  FRI: 'P',
  SAT: 'Szo',
  SUN: 'V',
  HOLIDAY: 'és munkaszüneti napokon',
};

const createSvgIcon = svgElement => (
  <SvgIcon>{React.createElement(svgElement)}</SvgIcon>
);
const flightIcon = (
  <FontIcon className="material-icons" size={12}>
    flight
  </FontIcon>
);

const styles = {
  chip: {
    height: 24,
    marginBottom: 4,
    marginRight: 4,
    display: 'inline-flex',
  },
  avatar: {
    width: '24px !important',
    height: '24px !important',
  },
  fontIcon: {
    fontSize: 18,
    padding: 3,
  },
  chipLabel: {
    display: 'inline-block',
    maxWidth: 150,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};

const opHoursFormatter = e =>
  e
    .replace('SR', 'napkelte')
    .replace('SS', 'napnyugta')
    .replace(/\b(\d{2})\b/g, '$1.')
    .replace(/(\d{2})(\d{2})/g, '$1:$2')
    .replace(/-/g, '–')
    .replace('.–', '–')
    .replace(MONTH_ORIG_RE, m => MONTH_SUBST[m])
    .replace(WEEKDAY_ORIG_RE, m => WEEKDAY_SUBST[m])
    .replace('H–P', 'hétköznap')
    .replace('Szo–V', 'hétvégén');

export const AIRSPACE_USE_TYPES = {
  UAV_FLIGHT: {
    label: 'Repülés pilóta nélküli légijárművel',
    icon: createSvgIcon(UavIcon),
  },
  CONVENTIONAL_FLIGHT: {
    label: 'Repülés hagyományos légijárművel',
    icon: flightIcon,
  },
  MILITARY: {
    label: 'Katonai műveletek',
    icon: createSvgIcon(MilitaryIcon),
  },
  GLIDING: {
    label: 'Siklórepülés',
    icon: createSvgIcon(GlidingIcon),
  },
  PARACHUTING: {
    label: 'Ejtőernyős ugrás',
    icon: createSvgIcon(ParachutingIcon),
  },
  AIR_DISPLAY: {
    label: 'Légibemutató',
    icon: createSvgIcon(AirDisplayIcon),
  },
  FIREWORKS: {
    label: 'Tűzijáték',
    icon: createSvgIcon(FireworksIcon),
  },
  MISC: {
    label: 'Egyéb',
    icon: flightIcon,
  },
};

export const AIRSPACE_TYPE_LABELS = {
  CTA: 'Egyéb polgári irányítói körzet',
  CTR: 'Repülőtéri irányító körzet',
  DA: 'Veszélyes légtér',
  DZ: 'Drop Zone légtér',
  MCTR: 'Katonai repülőtéri irányító körzet',
  MTMA: 'Katonai közelkörzeti irányító körzet',
  P: 'Tiltott légtér',
  PCA: 'Előzetes koordinációs légtér',
  RA: 'Korlátozott légtér',
  RCA: 'Csökkentett koordinációs légtér',
  TA: 'Eseti légtér',
  TIZ: 'Forgalmi tájékoztató körzet',
  TIZ_WO_AFIS: 'AFIS-t nem nyújtó forgalmi tájékoztató körzet',
  TMA: 'Közelkörzeti irányító körzet',
  TRA: 'Időszakosan korlátozott légtér',
  TRSA: 'Időszakosan korlátozott légtér',

  COORD: 'Koordinált légtér',
  ENV: 'Környezetvédelmi szempontból korlátozott légtér',
  AP: 'AFIS-t nem biztosító repteret körbevevő légtér',
  AEROB: 'Műrepülő légtér',
  DJI_FLYZONE: 'DJI Geo Zone',
  NODRONEZONE: 'No drone zone',

  UNK: 'Ismeretlen',
};

export const AIRCRAFT_TYPES = {
  glider: {
    label: 'Vitorlázórepülő',
    icon: createSvgIcon(GliderIcon),
  },
  airliner: {
    label: 'Repülőgép',
    icon: createSvgIcon(AirlinerIcon),
  },
  motorplane: {
    label: 'Kisrepülő',
    icon: createSvgIcon(MotorplaneIcon),
  },
  helicopter: {
    label: 'Helikopter',
    icon: createSvgIcon(HelicopterIcon),
  },
  paraglider: {
    label: 'Siklóernyő',
    icon: createSvgIcon(ParagliderIcon),
  },
  uav: {
    label: 'Pilóta nélküli légijármű',
    icon: createSvgIcon(UavPropsheetIcon),
  },
  unknown: {
    label: 'Egyéb légijármű',
    icon: createSvgIcon(MotorplaneIcon),
  },
  'ground-station': {
    label: 'Földi irányítóállomás',
    icon: createSvgIcon(GroundStationIcon),
  },
  'ground-vehicle': {
    label: 'Földi jármű',
    icon: createSvgIcon(GroundVehicleIcon),
  },
};

export const PROP_SHEET_FIELDS = {
  ap: {
    operator: {
      label: 'Üzembentartó',
      icon: 'person',
    },
    operator_contact: {
      label: 'Elérhetőség',
      icon: 'mail',
    },
    freq: {
      label: 'Frekvencia',
      icon: 'settings_input_antenna',
      content: (f: string) =>
        parseFloat(f) ? `${f} MHz` : <em>(ismeretlen)</em>,
    },
    web: {
      label: 'Weboldal',
      icon: 'explore',
      content: (f: string) => (
        <a
          href={f.match(/^https?:/) ? f : `http://${f}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {f}
        </a>
      ),
    },
  },

  as: {
    use_type: {
      label: 'Használat célja',
      icon: 'list',
      content: (u: string) => u && AIRSPACE_USE_TYPES[u].label,
    },
    altitude_range: {
      label: 'Magasság',
      icon: 'swap_vert',
      content: ([lower, upper]: [number, number], f) => {
        const lowerFormatted = formatAltitude(lower, true, f.lower_is_agl);
        const upperFormatted = formatAltitude(upper, true, f.upper_is_agl);
        return `${lowerFormatted} – ${upperFormatted}`;
      },
    },
    validity: {
      label: 'Érvényesség',
      icon: 'date_range',
      content: (v: Array<string>, f: feature) => {
        if (v.every(e => e === null) || f.source === 'DJI_FLYSAFE') {
          return <em>(állandó)</em>;
        } else if (v.includes(null)) {
          const suffix = v[0] === null ? '-ig' : '-tól';
          const date = new Date(v.filter(e => e)[0]).toLocaleDateString(
            'hu-HU',
            DATE_LOCALIZATION_OPTIONS
          );

          const dateWithRemovedEndDot = date.substring(0, date.length - 1);
          return `${dateWithRemovedEndDot}${suffix}`;
        } else if (v[0] === v[1]) {
          return new Date(v[0]).toLocaleDateString(
            'hu-HU',
            DATE_LOCALIZATION_OPTIONS
          );
        }
        return v
          .map(d =>
            new Date(d).toLocaleDateString('hu-HU', DATE_LOCALIZATION_OPTIONS)
          )
          .join(' – ');
      },
    },
    op_hours: {
      label: 'Üzemidő (UTC)',
      icon: 'timer',
      content: (d: ?string) => {
        if (!d) {
          return <em>(az érvényesség alatt folyamatos)</em>;
        }

        const items = d.split(',');

        if (items.length === 1) {
          return opHoursFormatter(items[0]);
        }
        return (
          <ul style={{ paddingLeft: 15 }}>
            {d
              .split(',')
              .map(e => (
                <li key={`propSheet-opHours-${e}`}>{opHoursFormatter(e)}</li>
              ))}
          </ul>
        );
      },
    },

    phone: {
      label: 'Telefonszám',
      icon: 'phone',
      content: (p: ?string, f) => {
        if (f.airspace_type !== 'TA') {
          return false;
        }

        if (!p) {
          return <em>(nincs megadva)</em>;
        }

        return <a href={`tel:${p}`}>{p}</a>;
      },
      featureFlag: 'hasSubscription',
    },
  },

  s: {
    use_type: {
      label: 'Igénylés célja',
      icon: 'list',
      content: (u: string) => u && AIRSPACE_USE_TYPES[u].label,
    },
    altitude_range: {
      label: 'Magasság (AMSL)',
      icon: 'swap_vert',
      content: (r: [number, number]) =>
        r.map(a => formatAltitude(a, true)).join(' – '),
    },
    date_range: {
      label: 'Érvényesség',
      icon: 'date_range',
      content: (v: Array<string>) => {
        const d = v.map(de => de.split(' ')[0]);
        if (d[0] === d[1]) {
          return new Date(d[0]).toLocaleDateString(
            'hu-HU',
            DATE_LOCALIZATION_OPTIONS
          );
        }
        return v
          .map((d2: string) =>
            new Date(d2).toLocaleDateString('hu-HU', DATE_LOCALIZATION_OPTIONS)
          )
          .join(' – ');
      },
    },
    time_range: {
      label: 'Üzemidő (UTC)',
      icon: 'timer',
      content: (d: string) => {
        const timeFormatter = t => {
          const d2 = new Date(t);
          return `${leftPad(d2.getHours())}:${leftPad(d2.getMinutes())}`;
        };

        return `${timeFormatter(d[0])} – ${timeFormatter(d[1])}`;
      },
    },
  },

  ac: {
    altitude: {
      label: 'Magasság (AMSL)',
      icon: 'swap_vert',
      content: (r: number) => formatAltitude(r, true),
    },
    ground_speed: {
      label: 'Föld feletti seb.',
      icon: 'trending_flat',
      content: (r: number, { aircraft_type }) =>
        aircraft_type === 'ground-station'
          ? undefined
          : `${Math.round(r * 1.94)} kt`,
    },
    climb_rate: {
      label: 'Vertikális seb.',
      icon: 'trending_up',
      content: (r: number, { aircraft_type }) =>
        aircraft_type === 'ground-station'
          ? undefined
          : `${Math.round(r * 196.85)} ft/min`,
    },
    receiver_name: {
      label: 'Vevő azonosítója',
      icon: 'wifi_tethering',
    },
    overlaps: {
      label: 'Átlapolások',
      icon: 'layers',
      content: overlaps =>
        !overlaps.length ? (
          <em>Nincs átlapoló légtér</em>
        ) : (
          sortBy(overlaps, o => o.style_category).map(asp => {
            const color = getFeatureColor(asp);
            const { red, green, blue } = hexRgb(color);
            const icon =
              asp.use_type in AIRSPACE_USE_TYPES ? (
                AIRSPACE_USE_TYPES[asp.use_type].icon
              ) : (
                <FontIcon
                  className="material-icons"
                  style={styles.fontIcon}
                  color="white"
                >
                  flight
                </FontIcon>
              );
            return (
              <Chip
                key={`overlap-chip-${asp.name}`}
                backgroundColor={`rgba(${red}, ${green}, ${blue}, .15)`}
                style={styles.chip}
                labelStyle={{ lineHeight: '24px' }}
              >
                <Avatar
                  size={24}
                  style={styles.avatar}
                  color="white"
                  backgroundColor={color}
                >
                  {icon}
                </Avatar>
                <span style={{ color, ...styles.chipLabel }}>{asp.name}</span>
              </Chip>
            );
          })
        ),
    },
    nearby_aircraft: {
      label: 'Közeli járművek',
      icon: 'warning',
      content: r =>
        !r.length ? (
          <em>Nincs légijármű a közelben</em>
        ) : (
          <React.Fragment>
            {r.slice(0, 5).map(ac => (
              <Chip
                key={`nearby-chip-${ac.name}`}
                backgroundColor="#FEF3E5"
                style={styles.chip}
                labelStyle={{ lineHeight: '24px' }}
              >
                <Avatar
                  size={24}
                  style={styles.avatar}
                  color="white"
                  backgroundColor="#FB8C02"
                >
                  <FontIcon
                    style={styles.fontIcon}
                    className="material-icons"
                    size={12}
                    color="white"
                  >
                    warning
                  </FontIcon>
                </Avatar>
                <span style={{ color: '#FB8C02', ...styles.chipLabel }}>
                  {ac.name}
                </span>
              </Chip>
            ))}
            {r.length > 5 && <em>és {r.length - 5} további</em>}
          </React.Fragment>
        ),
    },
  },
};



// WEBPACK FOOTER //
// ./utils/propSheet.js